import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Box,
  Alert,
  Chip,
  LinearProgress,
  Paper,
  Divider,
} from '@mui/material';
import { CheckCircle, Psychology } from '@mui/icons-material';
import { debounce } from 'lodash';
import {
  PatientAssessment,
  AssessmentTemplate,
  assessmentAPI,
  calculatePHQ9Score,
  calculateProgress,
  isAssessmentComplete,
} from '../../services/assessmentService';

interface PHQ9AssessmentProps {
  assessment: PatientAssessment;
  template: AssessmentTemplate;
  onComplete: () => void;
  onError: (error: string) => void;
}

const PHQ9Assessment: React.FC<PHQ9AssessmentProps> = ({
  assessment,
  template,
  onComplete,
  onError
}) => {
  const [responses, setResponses] = useState<Record<string, string>>(assessment.responses || {});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [score, setScore] = useState<{ totalScore: number; level: string; interpretation: string } | null>(null);
  const [progress, setProgress] = useState(assessment.progress || 0);

  // Auto-save debounced function
  const debouncedSave = useCallback(
    debounce(async (responses: Record<string, string>, progress: number) => {
      try {
        setSaving(true);
        await assessmentAPI.saveResponses(assessment.assessmentId, {
          responses,
          progress,
          status: progress > 0 ? 'in_progress' : 'assigned'
        });
      } catch (error) {
        // Don't show error for auto-save failures
      } finally {
        setSaving(false);
      }
    }, 2000),
    [assessment.assessmentId]
  );

  // Update score and progress when responses change
  useEffect(() => {
    const newProgress = calculateProgress(responses, template);
    setProgress(newProgress);

    // Calculate PHQ-9 score if we have enough responses
    const phq9Responses = Object.entries(responses).filter(([key]) => key.startsWith('phq9_q'));
    if (phq9Responses.length > 0) {
      const newScore = calculatePHQ9Score(responses);
      setScore(newScore);
    }

    // Auto-save if there are responses
    if (Object.keys(responses).length > 0) {
      debouncedSave(responses, newProgress);
    }
  }, [responses, template, debouncedSave]);

  const handleResponseChange = (questionId: string, value: string) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      // Validate all required questions are answered
      if (!isAssessmentComplete(responses, template)) {
        onError('Please answer all required questions before submitting.');
        return;
      }

      // Calculate final score
      const finalScore = calculatePHQ9Score(responses);

      // Submit assessment
      await assessmentAPI.submitAssessment(assessment.assessmentId, {
        responses,
        totalScore: finalScore.totalScore,
        scoreLevel: finalScore.level,
        scoreInterpretation: finalScore.interpretation
      });

      onComplete();
    } catch (error: any) {
      onError(error.response?.data?.message || 'Failed to submit assessment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getScoreColor = (level: string) => {
    switch (level) {
      case 'minimal': return 'success';
      case 'mild': return 'info';
      case 'moderate': return 'warning';
      case 'moderate-severe': return 'error';
      case 'severe': return 'error';
      default: return 'default';
    }
  };

  const isComplete = isAssessmentComplete(responses, template);

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Psychology sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" gutterBottom>
              PHQ-9 Depression Scale
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Over the last 2 weeks, how often have you been bothered by any of the following problems?
            </Typography>
          </Box>
        </Box>

        {/* Progress */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progress: {Math.round(progress * 100)}%
            </Typography>
            {saving && (
              <Typography variant="caption" color="text.secondary">
                Saving...
              </Typography>
            )}
          </Box>
          <LinearProgress variant="determinate" value={progress * 100} sx={{ height: 6, borderRadius: 3 }} />
        </Box>

        {/* Questions */}
        <Box sx={{ mb: 4 }}>
          {template.questions.map((question, index) => (
            <FormControl key={question.questionId} component="fieldset" sx={{ mb: 4, width: '100%' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {index + 1}. {question.questionText}
                  {question.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                </Typography>
                {question.helpText && (
                  <Typography variant="body2" color="text.secondary">
                    {question.helpText}
                  </Typography>
                )}
              </Box>

              <RadioGroup
                value={responses[question.questionId] || ''}
                onChange={(e) => handleResponseChange(question.questionId, e.target.value)}
                sx={{ pl: 2 }}
              >
                {question.options?.map((option, idx) => (
                  <FormControlLabel
                    key={idx}
                    value={idx.toString()}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">{option}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({idx} points)
                        </Typography>
                      </Box>
                    }
                    sx={{
                      mb: 1,
                      p: 1,
                      borderRadius: 1,
                      border: responses[question.questionId] === idx.toString() ? '2px solid' : '1px solid transparent',
                      borderColor: responses[question.questionId] === idx.toString() ? 'primary.main' : 'transparent',
                      bgcolor: responses[question.questionId] === idx.toString() ? 'primary.50' : 'transparent',
                      '&:hover': {
                        bgcolor: 'grey.50'
                      }
                    }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          ))}
        </Box>

        {/* Real-time Score Display */}
        {score && Object.keys(responses).length >= 9 && (
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>
              Current Assessment Results
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h4" color="primary">
                {score.totalScore}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                / 27 points
              </Typography>
              <Chip 
                label={score.level.toUpperCase().replace('-', ' ')} 
                color={getScoreColor(score.level)}
                variant="filled"
              />
            </Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Interpretation:</strong> {score.interpretation}
            </Typography>
            <Alert 
              severity={score.level === 'minimal' || score.level === 'mild' ? 'info' : 'warning'}
              sx={{ mt: 2 }}
            >
              {score.level === 'severe' 
                ? 'Severe depression symptoms detected. Please discuss these results with your healthcare provider immediately.'
                : score.level === 'moderate-severe' || score.level === 'moderate'
                ? 'Moderate to severe depression symptoms detected. Professional treatment is recommended.'
                : 'Your responses will be reviewed by your healthcare provider during your appointment.'
              }
            </Alert>
          </Paper>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isComplete && (
              <CheckCircle color="success" fontSize="small" />
            )}
            <Typography variant="body2" color={isComplete ? 'success.main' : 'text.secondary'}>
              {isComplete ? 'All questions answered' : `${9 - Object.keys(responses).length} questions remaining`}
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={!isComplete || submitting}
            startIcon={submitting ? null : <CheckCircle />}
            sx={{ minWidth: 200 }}
          >
            {submitting ? 'Submitting...' : 'Complete PHQ-9 Assessment'}
          </Button>
        </Box>

        {/* Help Text */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>About PHQ-9:</strong> The Patient Health Questionnaire (PHQ-9) is a validated screening tool for depression. 
            Your responses help healthcare providers understand your mental health status and determine appropriate treatment recommendations.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PHQ9Assessment;